import { motion, useMotionValue, useAnimationFrame } from "framer-motion"
import { FaSearch, FaMapMarkerAlt, FaCalendarAlt, FaUsers, FaChevronRight, FaArrowRight } from "react-icons/fa"

const FloatingElement = ({ children, phaseOffset = 0 }) => {
  const y = useMotionValue(0)

  useAnimationFrame((t) => {
    y.set(Math.sin(t / 1200 + phaseOffset) * 10)
  })

  return (
    <motion.div style={{ y }} className="relative z-10">
      {children}
    </motion.div>
  )
}

export default function FeaturesSection({
  handleViewAllLibraries,
  handleSignupClick,
  handleLearnMoreClick,
  libraries,
  currentLibraryIndex,
  LibraryCard,
}) {
  return (
    <>
      <motion.section
        className="mb-16 md:mb-32 relative z-10"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        viewport={{ once: true, amount: 0.2 }}
      >
        <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-center mb-8 md:mb-12 bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-purple-400">
          How It Works
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
          {[
            { icon: FaSearch, title: "Search", description: "Find libraries near you by city, area, or name" },
            { icon: FaMapMarkerAlt, title: "Choose", description: "Compare amenities, seats, and pricing" },
            { icon: FaCalendarAlt, title: "Subscribe", description: "Pick a flexible monthly or shift plan" },
            { icon: FaUsers, title: "Study", description: "Join a focused learning community" },
          ].map((step, index) => (
            <FloatingElement key={index} phaseOffset={index * 0.8}>
              <motion.div
                className="bg-white bg-opacity-10 backdrop-filter backdrop-blur-lg rounded-lg p-6 md:p-8 shadow-lg hover:shadow-xl transition-shadow duration-300"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true, amount: 0.2 }}
              >
                <step.icon className="text-4xl md:text-5xl text-blue-400 mb-4 md:mb-6" />
                <h3 className="text-xl md:text-2xl font-semibold mb-2 md:mb-4">{step.title}</h3>
                <p className="text-gray-300 text-base md:text-lg">{step.description}</p>
              </motion.div>
            </FloatingElement>
          ))}
        </div>
      </motion.section>

      <motion.section
        className="mb-16 md:mb-32"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        viewport={{ once: true, amount: 0.2 }}
      >
        <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-center mb-8 md:mb-12 bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-purple-400">
          Explore Our Libraries
        </h2>
        <div className="text-center">
          <p className="text-lg md:text-xl text-gray-300 mb-6 md:mb-8">
            Discover a wide range of libraries and find the perfect study space for you.
          </p>
          <motion.button
            onClick={handleViewAllLibraries}
            className="group bg-gradient-to-r from-blue-500 to-purple-600 text-white font-bold py-3 md:py-4 px-6 md:px-8 rounded-full shadow-lg transition duration-300 ease-in-out transform hover:scale-105 text-base md:text-lg focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-opacity-50"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            View All Libraries
            <FaChevronRight className="inline-block ml-2 transform group-hover:translate-x-1 transition-transform duration-200" />
          </motion.button>
        </div>
      </motion.section>

      <motion.section
        className="mb-16 md:mb-32"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        viewport={{ once: true, amount: 0.2 }}
      >
        <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-center mb-8 md:mb-12 bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-purple-400">
          Featured Library
        </h2>
        {libraries.length > 0 ? (
          <div className="p-4 md:p-8 transition-shadow duration-300">
            <LibraryCard library={libraries[currentLibraryIndex]} />
          </div>
        ) : (
          <p className="text-center text-gray-400">Loading libraries...</p>
        )}
      </motion.section>

      <motion.section
        className="mb-16 md:mb-32 relative z-10"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        viewport={{ once: true, amount: 0.2 }}
      >
        <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-center mb-8 md:mb-12 bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-purple-400">
          Why Choose The Library Hub?
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {[
            { title: "Thousands of Libraries", description: "Access to a vast network of study spaces" },
            { title: "Flexible Subscriptions", description: "Choose plans that fit your schedule and budget" },
            { title: "Multiple Time Slots", description: "Study when it's convenient for you" },
            { title: "Diverse Study Environments", description: "From quiet zones to collaborative spaces" },
            { title: "Amenities Galore", description: "High-speed Wi-Fi, comfortable seating, and more" },
            { title: "Community of Learners", description: "Connect with like-minded individuals" },
          ].map((feature, index) => (
            <FloatingElement key={index} phaseOffset={index * 0.8}>
              <motion.div
                className="bg-white bg-opacity-5 rounded-lg p-6 md:p-8 shadow-lg hover:shadow-xl transition-shadow duration-300"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true, amount: 0.2 }}
              >
                <h3 className="text-xl md:text-2xl font-semibold mb-2 md:mb-4">{feature.title}</h3>
                <p className="text-gray-300 text-base md:text-lg">{feature.description}</p>
              </motion.div>
            </FloatingElement>
          ))}
        </div>
      </motion.section>

      <motion.section
        className="mb-16 md:mb-32"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        viewport={{ once: true, amount: 0.2 }}
      >
        <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-center mb-6 md:mb-8 bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-purple-400">
          Ready to Get Started?
        </h2>
        <div className="text-center">
          <p className="text-lg md:text-xl text-gray-300 mb-6 md:mb-8">
            Join thousands of students who have found their perfect study space with The Library Hub.
          </p>
          <motion.button
            className="group bg-gradient-to-r from-blue-500 to-purple-600 text-white font-bold py-3 md:py-4 px-6 md:px-8 rounded-full shadow-lg transition duration-300 ease-in-out transform hover:scale-105 text-base md:text-lg focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-opacity-50"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={handleSignupClick}
          >
            Sign Up Now
            <FaArrowRight className="inline-block ml-2 transform group-hover:translate-x-1 transition-transform duration-200" />
          </motion.button>
        </div>
      </motion.section>

      <motion.section
        className="mb-16 md:mb-32"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        viewport={{ once: true, amount: 0.2 }}
      >
        <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-center mb-8 md:mb-12 bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-purple-400">
          Are You a Library Owner?
        </h2>
        <div className="text-center">
          <p className="text-lg md:text-xl text-gray-300 mb-6 md:mb-8">
            Discover the benefits of joining The Library Hub and expand your reach to more students.
          </p>
          <motion.button
            onClick={handleLearnMoreClick}
            className="group inline-flex items-center bg-gradient-to-r from-blue-500 to-purple-600 text-white font-bold py-3 md:py-4 px-6 md:px-8 rounded-full shadow-lg transition duration-300 ease-in-out transform hover:scale-105 text-base md:text-lg focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-opacity-50"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            Learn Why You Should Join
            <FaArrowRight className="ml-2 transform group-hover:translate-x-1 transition-transform duration-200" />
          </motion.button>
        </div>
      </motion.section>
    </>
  )
}
